import dayjs from "dayjs";
import backupAPI from "./api-backup";

// Base for our first-party proxy. In production the Vercel rewrite forwards
// `/api/*` to the Cloudflare Worker, so the default empty base = relative
// `/api/...` calls (same-origin, no CORS, no upstream URL visible in
// DevTools). For local dev against a directly-reachable worker, set
// `REACT_APP_API_BASE` to its full origin (e.g. http://localhost:8787).
import { toStationWall } from '../lib/charts/time';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
const api = (path) => `${API_BASE}${path}`;

export const getStationData = async (station_id) => {
    return await fetch(api(`/api/stations/${station_id}?t=${Date.now()}`))
        .then(response => response.json())
        .then(data => {
            data = data?.stations;
            const formatted = {};
            data?.columns?.forEach((item, index) => {
                formatted[item] = data.records[0][index];
            })
            formatted.last_update = dayjs().format("DD.MM.YYYY HH:mm");
            return formatted;
        }).catch((err) => {
            return backupAPI.getStationData(station_id);
        })
}

/** All stations with their safe-sector fields (Green / Yellow / Red), one request. */
export const getStations = async () => {
    const response = await fetch(api(`/api/stations`));
    if (!response.ok) throw new Error(`stations_http_${response.status}`);
    const payload = await response.json();
    const data = payload?.stations;
    const columns = data?.columns || [];
    return (data?.records || []).map((rec) => {
        const row = {};
        rec.forEach((v, i) => { row[columns[i]] = v; });
        return row;
    });
};

export const getNow = async (station_id) => {
    return await fetch(api(`/api/stations/${station_id}/now?t=${Date.now()}`))
        .then(response => response.json())
        .then(data => {
            data = data?.Now;
            const formatted = {};
            if (data?.records?.length > 0) {
                data?.columns?.forEach((item, index) => {
                    formatted[item] = data.records[0][index];
                })
            }
            formatted.last_update = dayjs().format("DD.MM.YYYY HH:mm");
            return formatted;
        }).catch((err) => {
            return backupAPI.getNow(station_id);
        })
}

export const getAllData = async (station_id) => {
    return await fetch(api(`/api/stations/${station_id}/history?t=${Date.now()}`))
        .then(response => response.json())
        .then(data => {
            data = data.view;

            const formatted = [];
            if (data?.records?.length > 0) {
                data?.records?.forEach((item) => {
                    let formattedData = {};
                    item.forEach((v, i) => {
                        formattedData[data?.columns[i]] = v
                        return formattedData;
                    })
                    formatted.push(formattedData);
                })
            }
            return formatted;
        }).catch((err) => {
            return backupAPI.getAllData(station_id);
        })
}

/**
 * Raw 1-minute readings for [fromMs, toMs) from the Flumen archive via the
 * worker's bounded `/readings` route. Bounds are sent as Belgrade wall time
 * (what Flumen filters on). No backup fallback — the caller shows an error.
 */
export const getReadings = async (station_id, fromMs, toMs, signal) => {
    const from = toStationWall(fromMs, 'YYYY-MM-DDTHH:mm');
    const to = toStationWall(toMs, 'YYYY-MM-DDTHH:mm');
    const response = await fetch(api(`/api/stations/${station_id}/readings?from=${from}&to=${to}`), { signal });
    if (!response.ok) throw new Error(`readings_http_${response.status}`);
    const payload = await response.json();
    const data = payload?.data;
    const columns = data?.columns || [];
    return (data?.records || []).map((rec) => {
        const row = {};
        rec.forEach((v, i) => { row[columns[i]] = v; });
        return row;
    });
};

export const getAvgWindDirectionData = async (station_id, interval = 15) => {
    return await fetch(api(`/api/stations/${station_id}/avg-wind?interval=${interval}&t=${Date.now()}`))
        .then(response => response.json())
        .then(data => {
            return data;
        }).catch((err) => {
            return backupAPI.getAvgWindDirectionData(station_id);
        })
}

export const getInterval = async (station_id, interval = 15) => {
    return await fetch(api(`/api/stations/${station_id}/interval/${interval}?t=${Date.now()}`))
        .then(response => response.json())
        .then(data => {
            data = data['Interval' + interval];
            const formatted = {};
            if (data?.records?.length > 0) {
                data?.columns?.forEach((item, index) => {
                    formatted[item] = data.records[0][index];
                })
            }
            return formatted;
        }).catch((err) => {
            return backupAPI.getInterval(station_id);
        })
}

export const getTrend = async (station_id) => {
    return await fetch(api(`/api/stations/${station_id}/trend?t=${Date.now()}`))
        .then(response => response.json())
        .then(data => {
            return data[0] || {};
        }).catch((err) => {
            return backupAPI.getTrend(station_id);
        })
}

export const getSkewT = async (lat, lng, date, time, model) => {
    const ymd = dayjs(date).format('YYYYMMDD');
    const hhmm = dayjs.utc(time).format('HHmm');
    const url = api(`/api/forecast/skewt/${ymd}/${hhmm}/${parseFloat(lat)}/${parseFloat(lng)}?region=${encodeURIComponent(model)}`);
    return await fetch(url)
        .then(response => response.json())
        .then(data => data)
        .catch((err) => {
            return backupAPI.getSkewT(lat, lng, date, time, model);
        });
}


export const getPointForecast = async (lat, lng, times, model, includeWindgram = false) => {
    return await fetch(api(`/api/forecast/point/${parseFloat(lat)}/${parseFloat(lng)}?region=${encodeURIComponent(model)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            "times": times,
            ...(includeWindgram && { include_windgram: true }),
        })
    })
        .then(response => response.json())
        .then(data => {
            return data;
        }).catch((err) => {
            return backupAPI.getPointForecast(lat, lng, times, model);
        })
}

export const getWindgram = async (lat, lng, hours, model) => {
    return await fetch(api(`/api/forecast/windgram/${parseFloat(lat)}/${parseFloat(lng)}?region=${encodeURIComponent(model)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours }),
    })
        .then((response) => response.json())
        .then((data) => data) // { data: [...], base: "627.9" }
        .catch((err) => null);
};

export default { getStationData, getStations, getNow, getAllData, getReadings, getAvgWindDirectionData, getInterval, getTrend, getSkewT, getPointForecast, getWindgram }
